import cx from 'classnames'
import { Heading } from 'blaze-ui/Heading/Heading'
import { Drawer } from 'components/Drawer'
import { DrawerProps } from 'components/Drawer/Root'
import Styles from './PaletteFieldDrawer.module.scss'
import { isEqual } from 'lodash'
import { forwardRef, useEffect, useRef, useState } from 'react'
import useButtonWithScrollCancel from 'hooks/useButtonWithScrollCancel'
import { useObjectRef } from '@react-aria/utils'
import scrollIntoView from 'scroll-into-view-if-needed'
import { Ref } from 'components/DesignContentPreview/Types'
import { Palette } from 'constants/palettes'
import { PaletteSwatch } from 'components/PaletteSwatch/PaletteSwatch'
import { zoomFrameIntoView } from 'components/Wizards/MobileWizards/utils/zoomFrameIntoView'

interface Props extends DrawerProps {
  initialValue: Palette
  onSelect: (palette: Palette) => void
  palettes: PaletteWithLabel[]
  editorRef?: Ref
}

interface SwatchProp {
  isSelected: boolean
  onSelect: (palette: Palette) => void
  palette: Palette
  label?: string
}

export type PaletteWithLabel = { label?: string; palette: Palette }

const Swatch = forwardRef<HTMLDivElement, SwatchProp>(({ label, palette, isSelected, onSelect }, fRef) => {
  const ref = useObjectRef(fRef)

  const { buttonProps } = useButtonWithScrollCancel(
    {
      onPress: () => {
        onSelect(palette)
      },
      elementType: 'div',
    },
    ref,
  )

  useEffect(() => {
    if (isSelected && ref.current) {
      scrollIntoView(ref.current, {
        scrollMode: 'if-needed',
        block: 'center',
        inline: 'center',
      })
    }
  }, [isSelected]) // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <div
      className={cx(Styles.swatch, { [Styles.selected]: isSelected })}
      {...buttonProps}
      ref={ref}
      data-testid="palette-swatch"
    >
      {label && (
        <div className={Styles.swatchLabel}>
          {label} {label} {label}
        </div>
      )}
      <div className={Styles.swatchContent}>
        <PaletteSwatch colors={palette} />
      </div>
    </div>
  )
})

const SNAP_POINT = 0.5

export const PaletteFieldDrawer = ({ palettes, initialValue, onSelect, editorRef, open, ...props }: Props) => {
  const [value, setValue] = useState(initialValue)
  const initialRender = useRef(true)

  const handleSelect = (palette: Palette) => {
    setValue(palette)
    onSelect(palette)
  }

  useEffect(() => {
    if (!open) {
      editorRef?.setCameraToCurrentFrame({ animate: true, isMobile: true })
    }
  }, [open, editorRef]) // eslint-disable-line react-hooks/exhaustive-deps

  if (initialRender.current && editorRef) {
    zoomFrameIntoView(editorRef, SNAP_POINT)
    initialRender.current = false
  }

  return (
    <Drawer.Root {...props} activeSnapPoint={SNAP_POINT} snapPoints={[SNAP_POINT]} open={open}>
      <Drawer.Portal>
        <div
          style={{
            pointerEvents: 'all',
            inset: 0,
            position: 'fixed',
            zIndex: 9999,
          }}
        />
        <Drawer.Content fullHeight color="var(--light-100)">
          <div data-vaul-no-drag={true}>
            <Drawer.InnerContent>
              <Drawer.Top>
                <div />
                <Drawer.Title asChild>
                  <Heading lineClamp={1} level={5}>
                    Change Colors
                  </Heading>
                </Drawer.Title>
                <Drawer.Close label="Close" variant="ghost" position="right" />
                <Drawer.Description />
              </Drawer.Top>

              <Drawer.ScrollableContainer snap={0.5}>
                <div className={Styles.grid}>
                  {palettes.map((palette, index) => (
                    <Swatch
                      key={index}
                      label={palette.label}
                      palette={palette.palette}
                      isSelected={isEqual(value, palette.palette)}
                      onSelect={handleSelect}
                    />
                  ))}
                </div>
              </Drawer.ScrollableContainer>
            </Drawer.InnerContent>
          </div>
        </Drawer.Content>
      </Drawer.Portal>
    </Drawer.Root>
  )
}
